// CouncilFeedbackSection.tsx — moderation-facing product feedback for moderators.
//
// Scoped server-side to the moderation categories (scope=council), so this view
// never carries product bugs or feature requests. Council is only signed-in
// gated (NOT staff-gated), so it deliberately exposes no staff-only control —
// status changes and internal notes live in the Owner tracker, and the server
// rejects them from a non-staff caller regardless of what this page renders.
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useFeedbackList } from '@/hooks/useFeedback';
import { FEEDBACK_CATEGORY_LABELS } from '@/core/models/feedback';
import { StatusBadge } from '@/features/feedback/StatusBadge';
import { relativeTime } from '@/features/feedback/feedbackMeta';

const Head = styled.div`
  margin-bottom: 14px;
`;

const Title = styled.h2`
  margin: 0 0 4px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Note = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.article`
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
`;

const Votes = styled.div`
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const VotesLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 400;
  color: ${({ theme }) => theme.color.textMuted};
`;

const TitleLink = styled(Link)`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Empty = styled.p`
  padding: 24px 12px;
  margin: 0;
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

export function CouncilFeedbackSection() {
  const { items, loadState } = useFeedbackList({ scope: 'council', sort: 'top' });
  const loading = loadState === 'loading' || loadState === 'idle';

  return (
    <section>
      <Head>
        <Title>Moderation feedback</Title>
        <Note>
          What people say about how moderation works here: policy, the report flow, and Council
          decisions. This is not a case queue: nothing here removes or restores content.
        </Note>
      </Head>

      {loading ? (
        <Empty aria-busy="true">Loading feedback…</Empty>
      ) : items.length === 0 ? (
        <Empty>No moderation feedback yet.</Empty>
      ) : (
        <Rows>
          {items.map((item) => (
            <Row key={item.id} data-testid={`council-feedback-${item.id}`}>
              <div>
                <Votes>{item.upvotes}</Votes>
                <VotesLabel>votes</VotesLabel>
              </div>
              <div>
                <TitleLink to={`/feedback/${item.id}`}>{item.title}</TitleLink>
                <Meta>
                  <StatusBadge status={item.status} />
                  <span>{FEEDBACK_CATEGORY_LABELS[item.category]}</span>
                  <span>{item.author.handle}</span>
                  <span>
                    {item.commentCount} {item.commentCount === 1 ? 'reply' : 'replies'}
                  </span>
                  <span>{relativeTime(item.createdAt)}</span>
                </Meta>
              </div>
            </Row>
          ))}
        </Rows>
      )}
    </section>
  );
}
